import { IBar, IFrontendResults, IStabilityVisualization } from "../interfaces/frontend/IFrontendResults";
import { useMemo } from "react";
import { AssociatedDrug, Conservation, Mutation, ResultInhibitor, Results, StabilityPrediction, Uniprot } from "../../../api/openapi-generator";
import IComparisonItem from "../interfaces/frontend/IComparisonItem";
import IResultInhibitor from "../interfaces/frontend/IResultInhibitor";
import ChangeEnum from "../interfaces/frontend/ChangeEnum";
import { topScoringInhibitors } from "../../../settings";

export function useFrontendResults(beResults: Results, uniprot: Uniprot) {
  return useMemo<IFrontendResults>(() => {
    return {
      uniprotAccession: beResults.uniprotAccession,
      proteinCode: beResults.proteinCode,
      sequence: beResults.sequence,
      mutation: beResults.mutation,
      mutationStr: `${beResults.mutation.wt}${beResults.mutation.position}${beResults.mutation.mut}`,
      structure: beResults.structure,
      prediction: derivePrediction(beResults.prediction ?? null),
      predictsnp: derivePrediction(beResults.predictsnp ?? null),
      foldx: deriveStability(beResults.foldx ?? null, beResults.rosetta?.value ?? null),
      rosetta: deriveStability(beResults.rosetta ?? null, beResults.foldx?.value ?? null),
      hope: beResults.hope ?? null,
      conservation: deriveConservation(beResults.conservation),
      catalyticResidues: deriveCatalyticResidues(beResults.pka, beResults.sequence, beResults.mutation),
      inhibitors: deriveInhibitors(beResults?.inhibitors ?? null, uniprot?.associatedDrugs ?? null),
    };
  }, [beResults, uniprot]);
}

const backgroundColors = {
  deleterious: '#E77E68',  // 10% lighter than $ll-deleterious-color (#E47057)
  noChange: '#9E9E9E',  // lighter than $ll-no-change-color to achieve good contrast
  confidence: '#90bde0',  // 60% lighter than $darkblue (#225378)
};

type DeriveConfidenceParams = {
  deleterious: boolean,
  confidence: number,
}

function deriveConfidenceBar(params: DeriveConfidenceParams): IBar {
  const confidence = Math.round(params.confidence);
  return [
    {
      width: confidence,
      backgroundColor: params.deleterious ? backgroundColors.deleterious : backgroundColors.noChange,
      className: null,
      content: { text: `${confidence}%`, align: "center" },
    },
    {
      width: 100 - confidence,
      backgroundColor: null,
      className: null,
      content: null,
    }
  ];
}

function derivePrediction<T extends DeriveConfidenceParams>(prediction: T | null): (T & {bar: IBar}) | null {
  return prediction && {
    ...prediction,
    bar: deriveConfidenceBar(prediction),
  };
}

function deriveStability(prediction: StabilityPrediction | null, otherValue: StabilityPrediction['value'] | null): IStabilityVisualization | null {
  if (prediction === null) {
    return null;
  }
  let maxAbsValue = Math.max(3, Math.abs(prediction.value));
  if (typeof otherValue === 'number') maxAbsValue = Math.max(maxAbsValue, Math.abs(otherValue));
  const ceiledMaxAbsValue = Math.ceil(maxAbsValue);
  const bounds = { minus: -ceiledMaxAbsValue, plus: ceiledMaxAbsValue };
  const fullSize = bounds.plus - bounds.minus;
  const textOutsideMinWidth = 15;
  const deleterious = prediction.value >= prediction.threshold;
  // width between 1 and 100
  const mainWidth = Math.round(100 / fullSize * Math.abs(Math.min(Math.max(prediction.value, bounds.minus), bounds.plus)));
  const leftWidth = 50 - (prediction.value < 0 ? mainWidth : 0);
  const rightWidth = 100 - mainWidth - leftWidth;
  const bar: IBar = [
    {
      width: leftWidth,
      backgroundColor: null,
      className: null,
      content: prediction.value < 0 && leftWidth >= textOutsideMinWidth ? {
        text: `${prediction.value}`,
        align: "right",
      } : null,
    },
    {
      width: mainWidth,
      backgroundColor: deleterious ? backgroundColors.deleterious : backgroundColors.noChange,
      className: prediction.value < 0 ? 'll-border-right' : 'll-border-left',
      content: leftWidth < textOutsideMinWidth || rightWidth < textOutsideMinWidth ? {
        text: `${prediction.value}`,
        align: "center",
      } : null,
    },
    {
      width: rightWidth,
      backgroundColor: null,
      className: null,
      content: prediction.value >= 0 && rightWidth >= textOutsideMinWidth ? {
        text: `${prediction.value}`,
        align: "left",
      } : null,
    }
  ];
  return { deleterious, bar, bounds }
}

function deriveConservation(conservation: Conservation): string | null {
  if (!conservation) {
    return null;
  }
  // ConSurf: COLOR: The color scale representing the conservation scores (9 - conserved, 1 - variable).
  const { color } = conservation;
  if (1 <= color && color <= 3) {
    return "Low";
  } else if (4 <= color && color <= 6) {
    return "Moderate";
  } else if (7 <= color && color <= 9) {
    return "High";
  }
  return null;
}

function deriveCatalyticResidues(
  pka: Results['pka'],
  sequence: string,
  mutation: Mutation,
): IComparisonItem[] {
  if (!pka) {
    return [];
  }
  return pka.map(({ position, wt: wtVal, mut: mutVal }) => ({
    name: mutation.position === position ? `${mutation.wt}${mutation.position}${mutation.mut}` : `${sequence[position - 1]}${position}`,
    wtVal,
    mutVal,
    large: Math.abs(wtVal - mutVal) > 0.3,
  }));
}

export function deriveInhibitors(
  inhibitorsBE: ResultInhibitor[] | null,
  associatedDrugs: AssociatedDrug[] | null,
): IResultInhibitor[] {
  // associatedDrugs are nullable, it that case proceed as if no drug is associated
  const assocDrugDbIdSet = new Set(associatedDrugs?.map(d => d.id) ?? [])
  const inhibitors: IResultInhibitor[] = (inhibitorsBE ?? []).map(inh => {
    const { diff } = inh.bindingEnergy;
    // Whenever the following thresholds are changed, update tooltip for inhibitors section.
    const diffClass =
      diff <= -2   ? ChangeEnum.DecreaseLarge :
      diff <= -0.5 ? ChangeEnum.DecreaseModerate :
      diff <   0.5 ? ChangeEnum.NoChange :
      diff <   2   ? ChangeEnum.IncreaseModerate :
                     ChangeEnum.IncreaseLarge;

    let databaseLink: IResultInhibitor['databaseLink'] = null;
    if (typeof inh.drugbankId === 'string') {
      databaseLink = {
        drugId: inh.drugbankId,
        database: 'drugbank',
      }
    } else if (inh.zincId !== null) {
      databaseLink = {
        drugId: inh.zincId,
        label: inh.zincIdShort,
        database: 'zinc',
      }
    }
    const associatedDrug = typeof inh.drugbankId === 'string' && assocDrugDbIdSet.has(inh.drugbankId)
    return {
      ...inh,
      bindingEnergyDiffClass: diffClass,
      databaseLink,
      inChart: false,
      associatedDrug,
    }
  });

  // sort by binding energy of mutant
  inhibitors.sort((a, b) => a.bindingEnergy.mut - b.bindingEnergy.mut);
  // ... but show associated drugs first (sort is stable since EcmaScript 2019)
  inhibitors.sort((a, b) => (+ b.associatedDrug) - (+ a.associatedDrug)); // unary + converts bool to number

  // we put top inhibitors to the chart
  inhibitors.slice(0, topScoringInhibitors.max).forEach(i => i.inChart = true);

  return inhibitors;
}
